import * as React from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import Alert from '@cloudscape-design/components/alert';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import ContentLayout from '@cloudscape-design/components/content-layout';
import FormField from '@cloudscape-design/components/form-field';
import Header from '@cloudscape-design/components/header';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Spinner from '@cloudscape-design/components/spinner';
import {
  ALL_ALERT_KINDS,
  ALL_ALERT_SEVERITIES,
  type AlertKind,
  type AlertSeverity,
} from '@etiquetachef/types';
import { AlertCard } from './AlertCard';
import { useAlerts, type AlertStatusFilter } from './useAlerts';

const KEY_KIND = 'alertKind';
const KEY_SEVERITY = 'alertSeverity';
const KEY_STATUS = 'alertStatus';

function parseList<T extends string>(raw: string | null, allowed: readonly T[]): readonly T[] {
  if (!raw) return [];
  return raw
    .split(',')
    .map((s) => s.trim())
    .filter((s): s is T => (allowed as readonly string[]).includes(s));
}

function parseStatus(raw: string | null): AlertStatusFilter {
  if (raw === 'ACKNOWLEDGED' || raw === 'ALL') return raw;
  return 'PENDING';
}

/**
 * Tela `/dashboard/alerts`: lista dos alertas de BI com filtro por tipo,
 * severidade e status, e o reconhecimento de cada um.
 *
 * Origem: sem issue — commit d4e0990a9, 2026-05-04 (Sprint 4.13, ver
 * `docs/sprint-4.13-alerts-dashboard.md`).
 *
 * Contrato: sem props. Os filtros moram na URL (`alertKind`, `alertSeverity`
 * — listas separadas por vírgula — e `alertStatus`); valor desconhecido na URL
 * é descartado e o status cai em `PENDING`, que é o default e por isso não vai
 * para a URL. Estados LOADING/ERROR/READY vêm de `useAlerts`.
 *
 * Usado por: `routes/router.tsx` (rota `dashboard/alerts`, dentro do
 * `AdminLayout`, só super-admin); o badge do menu (`AlertCountBadge`) leva
 * para cá.
 *
 * Arquitetura: só apresentação e estado de URL. A leitura é do `useAlerts`
 * (filtro todo client-side, #620) e a única escrita, o reconhecimento, é do
 * `AcknowledgeButton` dentro de cada `AlertCard`.
 *
 * Dicas: `kinds`/`severities` são arrays novos a cada render — o `useMemo`
 * abaixo não os estabiliza; quem evita o re-subscribe em loop é a chave em
 * string dentro do `useAlerts`. Não trocar aquela chave pelo array.
 *
 * Issues: #620.
 */
export function AlertsPage(): React.JSX.Element {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();

  const kinds = parseList<AlertKind>(params.get(KEY_KIND), ALL_ALERT_KINDS);
  const severities = parseList<AlertSeverity>(params.get(KEY_SEVERITY), ALL_ALERT_SEVERITIES);
  const status = parseStatus(params.get(KEY_STATUS));

  const filterInput = useMemo(() => ({ kinds, severities, status }), [kinds, severities, status]);
  const state = useAlerts(filterInput);

  function update(key: string, value: string): void {
    setParams((prev) => {
      const merged = new URLSearchParams(prev);
      if (value === '') merged.delete(key);
      else merged.set(key, value);
      return merged;
    });
  }
  function toggleKind(kind: AlertKind): void {
    const next = kinds.includes(kind) ? kinds.filter((k) => k !== kind) : [...kinds, kind];
    update(KEY_KIND, next.join(','));
  }
  function toggleSeverity(sev: AlertSeverity): void {
    const next = severities.includes(sev) ? severities.filter((s) => s !== sev) : [...severities, sev];
    update(KEY_SEVERITY, next.join(','));
  }
  function setStatusFilter(next: AlertStatusFilter): void {
    update(KEY_STATUS, next === 'PENDING' ? '' : next);
  }

  return (
    <ContentLayout
      header={<Header variant="h1" description={t('alerts.subtitle')}>{t('alerts.title')}</Header>}
    >
      <SpaceBetween size="l">
        <FormField label={t('alerts.filters.kind')}>
          <SpaceBetween direction="horizontal" size="xs">
            {ALL_ALERT_KINDS.map((kind) => (
              <Button
                key={kind}
                variant={kinds.includes(kind) ? 'primary' : 'normal'}
                onClick={(): void => {
                  toggleKind(kind);
                }}
              >
                {t(`alerts.kind.${kind}`)}
              </Button>
            ))}
          </SpaceBetween>
        </FormField>

        <FormField label={t('alerts.filters.severity')}>
          <SpaceBetween direction="horizontal" size="xs">
            {ALL_ALERT_SEVERITIES.map((sev) => (
              <Button
                key={sev}
                variant={severities.includes(sev) ? 'primary' : 'normal'}
                onClick={(): void => {
                  toggleSeverity(sev);
                }}
              >
                {t(`alerts.severity.${sev}`)}
              </Button>
            ))}
          </SpaceBetween>
        </FormField>

        <FormField label={t('alerts.filters.status')}>
          <SpaceBetween direction="horizontal" size="xs">
            {(['PENDING', 'ACKNOWLEDGED', 'ALL'] as AlertStatusFilter[]).map((s) => (
              <Button
                key={s}
                variant={status === s ? 'primary' : 'normal'}
                onClick={(): void => {
                  setStatusFilter(s);
                }}
              >
                {t(`alerts.status.${s}`)}
              </Button>
            ))}
          </SpaceBetween>
        </FormField>

        {state.kind === 'LOADING' && (
          <Box textAlign="center" padding="l">
            <Spinner size="large" />
          </Box>
        )}
        {state.kind === 'ERROR' && <Alert type="error">{state.error}</Alert>}
        {state.kind === 'READY' && state.alerts.length === 0 && (
          <Box color="text-body-secondary">{t('alerts.empty')}</Box>
        )}
        {state.kind === 'READY' && state.alerts.length > 0 && (
          <SpaceBetween size="m">
            {state.alerts.map((a) => (
              <AlertCard key={a.alertId} alert={a} />
            ))}
          </SpaceBetween>
        )}
      </SpaceBetween>
    </ContentLayout>
  );
}
